const RECENT_COLORS_KEY = 'velostruct-recent-colors';
const MAX_RECENT_COLORS = 16;

class ColorService {
  private recentColors: string[] = [];

  constructor() {
    this.loadRecentColors();
  }

  private loadRecentColors(): void {
    try {
      const stored = localStorage.getItem(RECENT_COLORS_KEY);
      if (stored) {
        this.recentColors = JSON.parse(stored);
      }
    } catch {
      this.recentColors = [];
    }
  }

  private saveRecentColors(): void {
    try {
      localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(this.recentColors));
    } catch {
      // localStorage quota exceeded — ignore
    }
  }

  getRecentColors(): string[] {
    return [...this.recentColors];
  }

  addRecentColor(color: string): void {
    const normalized = color.toUpperCase();
    this.recentColors = [
      normalized,
      ...this.recentColors.filter(c => c.toUpperCase() !== normalized),
    ].slice(0, MAX_RECENT_COLORS);
    this.saveRecentColors();
  }

  clearRecentColors(): void {
    this.recentColors = [];
    this.saveRecentColors();
  }

  hexToHsl(hex: string): [number, number, number] {
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return [0, 0, 50];
    
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    if (max === min) return [0, 0, Math.round(l * 100)];

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;

    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;

    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
  }

  hslToHex(h: number, s: number, l: number): string {
    const sNorm = s / 100;
    const lNorm = l / 100;

    const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lNorm - c / 2;

    let r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }

    const toHex = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  }

  lightness(hex: string): number {
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return 50;
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return (Math.max(r, g, b) + Math.min(r, g, b)) / 2 * 100;
  }

  isValidHex(hex: string): boolean {
    return /^#[0-9a-fA-F]{6}$/.test(hex);
  }

  normalizeHex(hex: string): string {
    if (hex.length === 4) {
      return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toUpperCase();
    }
    return hex.toUpperCase();
  }
}

export const colorService = new ColorService();
